import React, { useEffect, useState } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { Confirm } from './../../Commons/Modals/Confirm';
import { PremiumQR } from './../../Commons/PremiumQR';

import { GetRouletteConfig, SaveRouletteConfig, PrepareRoulette, SpinRoulette } from './../../../utilities/BL/Roulette';
// Reutilizamos la lógica de pantallas de la Tragamonedas porque es la misma infraestructura física de stands
import { GetActiveScreens, CreateMachineScreen, DeleteMachineScreen, IdentifyAllScreens } from './../../../utilities/BL/SlotMachine';

const Roulette = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [isLoading, setIsLoading] = useState(false);
    
    // Conexiones y Pantallas
    const [screensList, setScreensList] = useState([]);
    const [isMainExpanded, setIsMainExpanded] = useState(true);
    const [isStandsExpanded, setIsStandsExpanded] = useState(false);
    const [selectedStandId, setSelectedStandId] = useState(0);

    // Modales QR y Confirmaciones
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ message: '', action: null, textYes: '', textNo: 'Cancelar' });
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrTargetUrl, setQrTargetUrl] = useState('');
    const [tvTargetUrl, setTvTargetUrl] = useState('');
    const [qrTitle, setQrTitle] = useState('');

    // Configuración Ruleta Activa (Main o Stand)
    const [activeConfig, setActiveConfig] = useState({ targetScreenId: 1, spinDurationMs: 5000, scale: 100, winProbability: 30, prizes: [] });

    // Campos simples para un nuevo premio
    const [newText, setNewText] = useState('');
    const [newIsWinner, setNewIsWinner] = useState(true);

    useEffect(() => {
        loadScreensData();
    }, []);

    const loadScreensData = async () => {
        const result = await GetActiveScreens();
        if (result.success && result.data) {
            setScreensList(result.data);
            
            // Cargar por defecto la ruleta del estadio (1)
            await loadConfigForScreen(1);

            const stands = result.data.filter(s => s.id !== 1);
            if (stands.length > 0 && selectedStandId === 0) {
                setSelectedStandId(stands[0].id);
            }
        }
    };

    const loadConfigForScreen = async (screenId) => {
        showMessage('Cargando ruleta...', MESSAGE_TYPE.INFO);
        const res = await GetRouletteConfig(screenId);
        if (res && res.success && res.data) {
            setActiveConfig({
                targetScreenId: screenId,
                spinDurationMs: res.data.spinDurationMs || 5000,
                scale: res.data.scale || 100,
                winProbability: res.data.winProbability ?? 30,
                prizes: res.data.prizes || []
            });
        }
    };

    const handleTargetChange = async (screenId, isMain) => {
        if (isMain) {
            setIsMainExpanded(true);
            setIsStandsExpanded(false);
            await loadConfigForScreen(1);
        } else {
            setIsMainExpanded(false);
            setIsStandsExpanded(true);
            setSelectedStandId(screenId);
            await loadConfigForScreen(screenId);
        }
    };

    const handleSaveConfig = async (updatedConfig = activeConfig) => {
        setActiveConfig(updatedConfig);
        const res = await SaveRouletteConfig(updatedConfig);
        if (res.success) showMessage('✅ Ruleta actualizada', MESSAGE_TYPE.SUCCESS);
        else showMessage('Error al guardar', MESSAGE_TYPE.ERROR);
    };

    const handleAddPrize = (e) => {
        e.preventDefault();
        if (!newText.trim()) return;

        const newPrize = {
            id: crypto.randomUUID().replace(/-/g, ''),
            text: newText.trim(),
            isWinner: newIsWinner
        };

        const updated = { ...activeConfig, prizes: [...activeConfig.prizes, newPrize] };
        handleSaveConfig(updated);
        setNewText('');
        setNewIsWinner(true);
    };

    const handleDeletePrize = (id) => {
        const updated = { ...activeConfig, prizes: activeConfig.prizes.filter(p => p.id !== id) };
        handleSaveConfig(updated);
    };

    // --- FUNCIONES DE PANTALLAS (QR Y STANDS) ---
    const handleAddScreen = async () => {
        const screenName = window.prompt("Nombre del nuevo stand (Ej: Entrada Sur):");
        if (!screenName) return;
        showMessage('Creando pantalla...', MESSAGE_TYPE.INFO);
        const result = await CreateMachineScreen(screenName.trim());
        if (result.success) {
            await loadScreensData();
            handleTargetChange(result.data.id, false);
            openQRModal(result.data.id, result.data.name);
        } else showMessage(result.message, MESSAGE_TYPE.ERROR);
    };

    const confirmDeleteScreen = () => {
        setConfirmConfig({
            message: `¿Seguro que deseas eliminar este stand?`,
            action: async () => {
                const res = await DeleteMachineScreen(selectedStandId);
                if(res.success){
                    showMessage('Stand eliminado', MESSAGE_TYPE.SUCCESS);
                    loadScreensData();
                    handleTargetChange(1, true); // Regresar al estadio
                } else showMessage(res.message, MESSAGE_TYPE.ERROR);
                return res;
            },
            textYes: 'Sí, Eliminar',
            textNo: 'Cancelar'
        });
        setShowConfirmModal(true);
    };

    const openQRModal = (screenId, name) => {
        const tvUrl = (screenId === 1) ? `${window.location.origin}/screen` : `${window.location.origin}/pantalla-stand-roulette/${screenId}`;
        const playerUrl = `${window.location.origin}/roulette?screenId=${screenId}`;
        
        setTvTargetUrl(tvUrl);
        setQrTargetUrl(playerUrl);
        setQrTitle(`Jugar Ruleta: ${name}`);
        setShowQRModal(true);
    };

    // Acciones Remotas
    const triggerSpin = async () => {
        showMessage('Girando...', MESSAGE_TYPE.INFO);
        await SpinRoulette({ targetScreenId: activeConfig.targetScreenId, playerName: "Admin" });
    };

    const triggerReset = async () => {
        showMessage('Reiniciando...', MESSAGE_TYPE.INFO);
        await PrepareRoulette(activeConfig.targetScreenId);
    };

    return (
        <SecondaryBase actionToExecute={getOutOfTheDynamic}>
            
            {showConfirmModal && <Confirm message={confirmConfig.message} actionToYesButton={confirmConfig.action} textYesButton={confirmConfig.textYes} textNoButton={confirmConfig.textNo} setShowModal={setShowConfirmModal} />}

            {showQRModal && (
                <>
                    <div className="cModal__Background" onClick={() => setShowQRModal(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div className="cModal__Container__Content position-relative p-0" style={{ background: 'transparent', boxShadow: 'none' }}>
                            <button type="button" className="btn-close position-absolute top-0 end-0 m-3 bg-dark p-2 rounded-circle" onClick={() => setShowQRModal(false)} style={{zIndex: 20}}></button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1"><i className="bi bi-tv"></i> Abre este link en tu pantalla:</span>
                                <span className="fw-bold user-select-all">{tvTargetUrl}</span>
                            </div>
                            <PremiumQR url={qrTargetUrl} title={qrTitle} />
                        </div>
                    </div>
                </>
            )}

            <div className="mb-4 text-center">
                <p className='fs-2'>Ruleta de la Fortuna</p>
            </div>

            {/* BLOQUE 1: ESTADIO */}
            <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden">
                <div 
                    className={`card-header p-3 cursor-pointer d-flex justify-content-between align-items-center ${isMainExpanded ? 'bg-secondary text-white' : 'bg-light text-dark'}`}
                    onClick={() => handleTargetChange(1, true)}
                >
                    <h5 className="m-0 fw-bold"><i className="bi bi-projector-fill me-2"></i> 🏟️ Pantalla principal</h5>
                    <button className="btn btn-success fw-bold" onClick={triggerReset}>
                        <i className="bi bi-display"></i> Mostrar ruleta en pantalla
                    </button>
                </div>
            </div>

            <div className="card shadow-sm border-0 rounded-4 p-4 bg-white mb-4">
                <h5 className="fw-bold mb-3"><i className="bi bi-gear-fill"></i> Configuraciones</h5>
                
                {/* PROBABILIDAD DE GANAR (Igual a SlotMachine) */}
                <div className="mb-4">
                    <label className="form-label fw-bold text-dark">
                        Probabilidad de ganar: <span className="text-primary">{activeConfig.winProbability}%</span>
                    </label>
                    <div className="d-flex gap-2 mb-2">
                        <button className={`btn btn-sm ${activeConfig.winProbability === 0 ? 'btn-danger fw-bold' : 'btn-outline-danger'}`} onClick={() => handleSaveConfig({...activeConfig, winProbability: 0})}>Nunca (0%)</button>
                        <button className={`btn btn-sm ${activeConfig.winProbability === 30 ? 'btn-warning fw-bold text-dark' : 'btn-outline-warning text-dark'}`} onClick={() => handleSaveConfig({...activeConfig, winProbability: 30})}>Difícil (30%)</button>
                        <button className={`btn btn-sm ${activeConfig.winProbability === 50 ? 'btn-primary fw-bold' : 'btn-outline-primary'}`} onClick={() => handleSaveConfig({...activeConfig, winProbability: 50})}>50/50 (50%)</button>
                        <button className={`btn btn-sm ${activeConfig.winProbability === 100 ? 'btn-success fw-bold' : 'btn-outline-success'}`} onClick={() => handleSaveConfig({...activeConfig, winProbability: 100})}>Siempre (100%)</button>
                    </div>
                    <input 
                        type="range" 
                        className="form-range" 
                        min="0" max="100" step="5" 
                        value={activeConfig.winProbability} 
                        onChange={(e) => setActiveConfig({...activeConfig, winProbability: Number(e.target.value)})}
                        onMouseUp={() => handleSaveConfig()} 
                    />
                </div>

                {/* ESCALA VISUAL / ZOOM (Igual a SlotMachine) */}
                <div className="mb-4">
                    <label className="form-label fw-bold text-dark">
                        Tamaño / Escala de la ruleta: <span className="text-primary">{activeConfig.scale}%</span>
                    </label>
                    <div className="d-flex align-items-center gap-2">
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => handleSaveConfig({...activeConfig, scale: Math.max(50, activeConfig.scale - 10)})}><i className="bi bi-dash-lg"></i></button>
                        <input 
                            type="range" 
                            className="form-range flex-grow-1" 
                            min="50" max="150" step="10" 
                            value={activeConfig.scale} 
                            onChange={(e) => setActiveConfig({...activeConfig, scale: Number(e.target.value)})} 
                            onMouseUp={() => handleSaveConfig()} 
                        />
                        <button className="btn btn-outline-secondary btn-sm" onClick={() => handleSaveConfig({...activeConfig, scale: Math.min(150, activeConfig.scale + 10)})}><i className="bi bi-plus-lg"></i></button>
                    </div>
                </div>

                <div className="d-flex flex-column gap-2">
                    <button className="btn btn-outline-success fw-bold flex-grow-1" onClick={triggerReset}>
                        Aplicar escala
                    </button>
                    <button className="btn btn-outline-primary fw-bold" onClick={(e) => { e.stopPropagation(); openQRModal(activeConfig.targetScreenId, isMainExpanded ? 'Pantalla Principal' : 'Stand'); }}>
                        <i className="bi bi-qr-code"></i> Instrucciones de conexión
                    </button>
                </div>
            </div>

            {/* ZONA DE EDICIÓN */}
            <div className="row g-4 mt-2">
                <div className="col-12"><h4 className="fw-bold text-muted border-bottom pb-2">Configurando: {isMainExpanded ? 'Pantalla del Estadio' : screensList.find(s => s.id === selectedStandId)?.name}</h4></div>
                
                <div className="col-md-5">
                    <div className="card shadow-sm border-0 rounded-4 p-4 bg-white mb-4">
                        <h5 className="fw-bold border-bottom pb-2 text-dark"><i className="bi bi-pie-chart-fill text-success"></i> Agregar elemento a ruleta</h5>
                        <form onSubmit={handleAddPrize} className="mt-3">
                            <div className="mb-3">
                                <label className="form-label small fw-bold text-muted">Nombre del elemento</label>
                                <input type="text" className="form-control" placeholder="Ej: Gorra Oficial" value={newText} onChange={(e) => setNewText(e.target.value)} required />
                            </div>
                            <div className="form-check form-switch mb-3">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    role="switch" 
                                    id="isWinnerSwitch" 
                                    checked={newIsWinner} 
                                    onChange={(e) => setNewIsWinner(e.target.checked)} 
                                />
                                <label className="form-check-label small fw-bold text-muted" htmlFor="isWinnerSwitch">
                                    ¿Es premio real?
                                </label>
                                <div className="form-text text-muted">Si está desactivado, se considerará "Sigue participando" (intento fallido).</div>
                            </div>
                            <button type="submit" className="btn btn-outline-success w-100 fw-bold"><i className="bi bi-plus-lg"></i> Agregar a la Ruleta</button>
                        </form>
                    </div>
                </div>

                <div className="col-md-7">
                    <div className="card shadow-sm border-0 rounded-4 p-4 bg-white h-100">
                        <h5 className="fw-bold border-bottom pb-2 text-dark mb-3"><i className="bi bi-list-ul text-warning"></i> Elementos actuales en ruleta</h5>

                        <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            <table className="table table-hover align-middle">
                                <thead className="table-light small">
                                    <tr>
                                        <th>Nombre del elemento</th>
                                        <th>Tipo de elemento</th>
                                        <th className="text-end">Borrar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeConfig.prizes.length === 0 ? (
                                        <tr><td colSpan="3" className="text-center py-4 text-muted">Ruleta vacía. Agrega rebanadas a la izquierda.</td></tr>
                                    ) : (
                                        activeConfig.prizes.map((prize) => (
                                            <tr key={prize.id}>
                                                <td className="fw-bold">{prize.text}</td>
                                                <td>
                                                    <span className={`badge ${prize.isWinner ? 'bg-success' : 'bg-secondary'}`}>
                                                        {prize.isWinner ? '🏆 Premio Real' : '❌ Sigue participando'}
                                                    </span>
                                                </td>
                                                <td className="text-end"><button className="btn btn-outline-danger btn-sm border-0" onClick={() => handleDeletePrize(prize.id)}><i className="bi bi-trash-fill"></i></button></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-auto pt-4">
                            <div className="d-flex flex-column gap-3">
                                <button className="btn btn-success flex-grow-1 fw-bold py-2" onClick={triggerSpin}>
                                    <i className="bi bi-play-fill"></i> ¡GIRAR RULETA!
                                </button>

                                <button className="btn btn-outline-primary fw-bold" onClick={triggerReset}>
                                    <i className="bi bi-display"></i> Actualizar/Mostrar ruleta en pantalla
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* BLOQUE 2: STANDS EXTRA */}
            <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden">
                <div 
                    className={`card-header p-3 cursor-pointer d-flex justify-content-between align-items-center ${isStandsExpanded ? 'bg-secondary text-white' : 'bg-light text-dark'}`}
                    onClick={() => { if(selectedStandId !== 0) handleTargetChange(selectedStandId, false); else setIsStandsExpanded(!isStandsExpanded); }}
                >
                    <h5 className="m-0 fw-bold"><i className="bi bi-tv-fill me-2"></i> 📺 Stands o Pantallas Extra</h5>
                    <i className={`bi fs-5 ${isStandsExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                </div>

                {isStandsExpanded && (
                    <div className="card-body bg-light">
                        <div className="d-flex align-items-center mb-3 bg-white p-3 rounded border shadow-sm">
                            <label className="me-3 fw-bold text-dark mb-0">Selecciona el stand:</label>
                            <select className="form-select fw-bold border-1 text-primary w-auto me-3" value={selectedStandId} onChange={(e) => handleTargetChange(Number(e.target.value), false)}>
                                {screensList.filter(s => s.id !== 1).length === 0 && <option value="0">-- No hay stands --</option>}
                                {screensList.filter(s => s.id !== 1).map(s => (
                                    <option key={s.id} value={s.id}>{s.isOnline ? '🟢' : '🔴'} {s.name}</option>
                                ))}
                            </select>
                            
                            <button className="btn btn-dark btn-sm fw-bold me-2" onClick={handleAddScreen}>
                                <i className="bi bi-plus-lg"></i> Crear Stand
                            </button>

                            {selectedStandId !== 0 && (
                                <>
                                    <button className="btn btn-outline-primary btn-sm fw-bold me-2" onClick={() => { const stand = screensList.find(s => s.id === selectedStandId); openQRModal(stand.id, stand.name); }}>
                                        <i className="bi bi-qr-code"></i> QR de este Stand
                                    </button>
                                    <button className="btn btn-outline-danger btn-sm ms-auto fw-bold" onClick={confirmDeleteScreen}>
                                        <i className="bi bi-trash-fill"></i> Eliminar
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* BOTÓN GLOBAL DE IDENTIFICACIÓN */}
            <div className="text-center mt-5">
                <button className="btn btn-outline-dark fw-bold shadow-sm" onClick={() => { IdentifyAllScreens(); showMessage('Señal enviada...', MESSAGE_TYPE.INFO); }}>
                    <i className="bi bi-search"></i> Identificar pantallas físicas (Hacer parpadear)
                </button>
            </div>
        </SecondaryBase>
    );
};

export { Roulette };
